import { html, View } from 'rune-ts';
import klass from './BrandCommerceSection.module.scss';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle';
import {
  ArrowPlugin,
  SeeMoreLinkPlugin,
} from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { SwiperView } from '@services/MarppleShop/renderApp/components/cells/Swiper/Swiper.ts';
import {
  ProductCardView,
  ProductLikeToggleEvent,
} from '@services/MarppleShop/renderApp/components/cells/ProductCard/ProductCardView.ts';
import {
  ProductCardViewData,
  ProductCardOption,
  ProductCardSize,
  ProductCardTheme,
} from '@services/MarppleShop/features/ProductList/type';
import { makeSwiperAutoPlayOption } from '@services/MarppleShop/renderApp/pages/Main/utils/swiper-option.ts';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { createBrandDealUrlBuilder } from '@services/MarppleShop/features/ProductList/util';

export type BrandCommerceSectionData = {
  products: ProductCardViewData[];
};

export type BrandCommerceSectionOption = {
  is_mobile: boolean;
};

export class BrandCommerceSectionView extends View<BrandCommerceSectionData> {
  private readonly swiperView: SwiperView<ProductCardViewData, typeof ProductCardView>;

  constructor(
    data: BrandCommerceSectionData,
    private option: BrandCommerceSectionOption,
  ) {
    super(data, option);

    const card_option: ProductCardOption = {
      resize_width: 600,
      theme: ProductCardTheme.dark,
      size: option.is_mobile ? ProductCardSize.small : ProductCardSize.large,
      card_klass: klass.product_card,

      // is_lazy 좌우로 적용하는 옵션도 필요할 듯 ...
      is_lazy: false,
    };

    this.swiperView = new SwiperView(
      makeSwiperAutoPlayOption({
        is_mobile: this.option.is_mobile,
      }),
      {
        swiper: klass.product_cards_swiper,
        wrapper: klass.product_cards_container,
      },
      ProductCardView,
      this.data.products,
      card_option,
    );
  }

  override template() {
    const { is_mobile } = this.option;
    /**
     *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
     *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
     */
    const href = createBrandDealUrlBuilder().setRankType('MONTHLY').build();
    return html`
      <div class="${klass.container}">
        <div class="${klass.content_wrap}">
          <!-- 타이틀 -->
          <div class="${klass.title_wrap}">
            ${new SectionTitle({
              title_props: {
                title: ET('mps2::main::brand_commerce::title'),
                size: is_mobile ? 'medium' : 'xLarge',
                theme: 'dark',
                tooltip: new Tooltip({
                  id: 'BrandCommerceSectionView',
                  size: is_mobile ? 'small' : 'medium',
                  content: html`${html.preventEscape(
                    UtilS.escape(ET('mps2::main::brand_commerce::tooltip'), true),
                  )}`,
                }),
              },
              plugins: [
                ...(is_mobile && this.data.products.length > 4
                  ? [
                      new SeeMoreLinkPlugin({
                        see_more_link: href,
                        size: this.option.is_mobile ? 'xSmall' : 'medium',
                        theme: 'dark',
                      }),
                    ]
                  : [
                      new SeeMoreLinkPlugin({
                        see_more_link: href,
                        size: this.option.is_mobile ? 'xSmall' : 'medium',
                        theme: 'dark',
                      }),
                      new ArrowPlugin({
                        theme: 'dark',
                        size: is_mobile ? 'large' : 'xLarge',
                        leftClickFn: () => {
                          this.swiperView.prev();
                        },
                        rightClickFn: () => {
                          this.swiperView.next();
                        },
                      }),
                    ]),
              ],
            })}
          </div>

          <!-- 상품 목록 with swiper -->
          ${this.swiperView}
        </div>
      </div>
    `;
  }

  public static isVisible(data: BrandCommerceSectionData): boolean {
    return data.products.length >= 1 && ET.lang === 'kr';
  }

  protected override onRender(): void {
    super.onRender();

    this.delegate(ProductLikeToggleEvent, ProductCardView, async (_, view) => {
      await view.toggleLikeButton();
    });
  }
}
